import React, { memo } from 'react';
import { useRouteMatch } from 'react-router';
import { Redirect, Switch } from 'react-router-dom';
import { ChappiChatbot } from '../common/chappi/chappi-chatbot';
import { EnvironmentService } from '../common/environment/environment-service';
import { DashboardRouteProps, dashboardRoutes } from '../common/routes';
import { useListUserViewDashboards } from '../common/v2/api/dashboards/hooks';
import { BetterLoadingOverlay } from '../common/v2/common/components/BetterLoading';
import { Dashboard } from '../common/v2/common/components/dashboards/types';
import { DEFAULT_LANDING_PAGE } from '../common/v2/constants';
import {
  useEnvironmentServiceContext,
  useFeatureFlagsServiceContext,
  useLatestPreferencesContext,
} from '../common/v2/context/contexts';
import { DashboardContextProvider } from '../common/v2/context/DashboardContextProvider';
import { LegacyDomainScopedContextProvider } from '../common/v2/context/LegacyDomainScopedContextProvider';
import { PrivateRoute } from './PrivateRoute';

// this override is just for dev/debug purposes
const allowAllDashboardRoutes = false;

const getPermittedDashboardRoutes = (
  allowedDashboards: Dashboard[],
  environmentService: EnvironmentService
) => {
  const permittedDashboards = allowedDashboards.filter((db) => db.enabled);
  let permittedDashboardRoutes: DashboardRouteProps[];
  if (environmentService.isLocalhost && allowAllDashboardRoutes) {
    permittedDashboardRoutes = dashboardRoutes;
  } else {
    permittedDashboardRoutes = dashboardRoutes.filter(
      (route) =>
        permittedDashboards &&
        permittedDashboards.some(({ link }) => {
          return (
            route.path && route.path.includes(link.replace('/dashboard/', ''))
          );
        })
    );
  }
  return permittedDashboardRoutes;
};

const DashboardRoutesInner = memo(() => {
  console.log2('DashboardRoutes');
  const latestPreferences = useLatestPreferencesContext();
  const companyLandingPage =
    latestPreferences.customLandingPage ?? DEFAULT_LANDING_PAGE;
  const environmentService = useEnvironmentServiceContext();
  const { dashboards: allowedDashboards, isLoading } =
    useListUserViewDashboards();
  const { path, url } = useRouteMatch();
  if (isLoading) {
    return (
      <BetterLoadingOverlay
        debugString="DashboardRoutes"
        isPending={isLoading}
      />
    );
  }
  const permittedDashboardRoutes = getPermittedDashboardRoutes(
    allowedDashboards,
    environmentService
  );
  console.log2('DashboardRoutes', {
    permittedDashboardRoutes,
    allowedDashboards,
    path,
    url,
  });

  return (
    <Switch>
      {permittedDashboardRoutes.map((route) => {
        return (
          <PrivateRoute key={route.id} component={route.comp()} {...route} />
        );
      })}
      <Redirect from="*" to={companyLandingPage} />
    </Switch>
  );
});
DashboardRoutesInner.displayName = 'DashboardRoutesInner';

export const DashboardRoutes = () => {
  const featureFlagsService = useFeatureFlagsServiceContext();
  return (
    <DashboardContextProvider>
      <LegacyDomainScopedContextProvider>
        <DashboardRoutesInner />
      </LegacyDomainScopedContextProvider>
      {featureFlagsService.isChappiEnabled() && <ChappiChatbot />}
    </DashboardContextProvider>
  );
};
